/**
 * Trade-in: the calculator on the programme page (sections/cz-trade-in-calc.liquid) and the
 * request form (sections/cz-trade-in-form.liquid).
 *
 * Both read the same parameters from theme settings (snippets/courtzone-trade-in-config.liquid):
 *   estimate = price of the new racket × condition % × year multiplier × brand multiplier,
 * rounded to 100 ₴, shown as a ±10 % range. A 0 % condition, a sum under 300 ₴ or over the
 * maximum (Theme settings → Trade-in, 50 000 ₴) is «індивідуально».
 *
 * The calculator hands its choice to the form through the URL (?brand=…&year=…&cond=…&price=…),
 * so the buyer doesn't answer the same questions twice.
 */

/** @typedef {[string, number]} Pair */
/** @typedef {{ brands: Pair[], years: Pair[], conditions: Pair[], priceMin: number, priceMax: number, priceDefault: number, maxEstimate: number }} Config */

/** @returns {Config} */
function readConfig() {
  const fallback = { brands: [], years: [], conditions: [], priceMin: 4000, priceMax: 20000, priceDefault: 15500, maxEstimate: 50000 };
  const element = document.querySelector('[data-trade-in-config]');
  try {
    return { ...fallback, ...JSON.parse(element?.textContent || '{}') };
  } catch {
    return fallback;
  }
}

/** @param {number} value */
const formatMoney = (value) => `${String(Math.round(value)).replace(/\B(?=(\d{3})+(?!\d))/g, ' ')} ₴`;

/** @param {Pair[]} list @param {string} name */
const factor = (list, name) => list.find(([label]) => label === name)?.[1];

/**
 * @param {Config} config
 * @param {{ brand: string, year: string, condition: string, price: number }} choice
 */
function estimate(config, { brand, year, condition, price }) {
  const percent = factor(config.conditions, condition) ?? 0;
  const share = (percent / 100) * (factor(config.years, year) ?? 1) * (factor(config.brands, brand) ?? 1);
  const amount = Math.round((price * share) / 100) * 100;
  return {
    share,
    amount,
    individual: percent === 0 || (price > 0 && (amount < 300 || amount > config.maxEstimate)),
    low: Math.round((amount * 0.9) / 100) * 100,
    high: Math.round((amount * 1.1) / 100) * 100,
  };
}

/** @param {ParentNode} root @param {string} name */
function checkedValue(root, name) {
  const input = root.querySelector(`input[name="${name}"]:checked`);
  return input instanceof HTMLInputElement ? input.value : '';
}

/** @param {ParentNode} root @param {string} selector @param {string} text */
function setText(root, selector, text) {
  root.querySelectorAll(selector).forEach((element) => {
    element.textContent = text;
  });
}

class CzTradeInCalc extends HTMLElement {
  connectedCallback() {
    this.config = readConfig();
    this.addEventListener('input', this.update);
    this.addEventListener('change', this.update);
    this.update();
  }

  disconnectedCallback() {
    this.removeEventListener('input', this.update);
    this.removeEventListener('change', this.update);
  }

  update = () => {
    const config = this.config || readConfig();
    const range = this.querySelector('input[name="price"]');
    const choice = {
      brand: checkedValue(this, 'brand'),
      year: checkedValue(this, 'year'),
      condition: checkedValue(this, 'cond'),
      price: range instanceof HTMLInputElement ? Number(range.value) : config.priceDefault,
    };
    const result = estimate(config, choice);
    const { individualText = 'індивідуально', individualNote = 'потребує огляду експерта', shareText = '[pct] від ціни нової' } =
      this.dataset;

    setText(this, '[data-price]', formatMoney(choice.price));
    setText(this, '[data-brand]', choice.brand);
    setText(this, '[data-year]', choice.year);
    setText(this, '[data-cond]', choice.condition);
    setText(this, '[data-estimate]', result.individual ? individualText : `≈ ${formatMoney(result.amount)}`);
    setText(
      this,
      '[data-estimate-range]',
      result.individual
        ? individualNote
        : `${formatMoney(result.low)} – ${formatMoney(result.high)} · ${shareText.replace('[pct]', `${Math.round(result.share * 100)} %`)}`
    );

    // The range's filled part, for browsers without a native progress track.
    if (range instanceof HTMLInputElement) {
      const filled = ((choice.price - Number(range.min)) / (Number(range.max) - Number(range.min))) * 100;
      range.style.setProperty('--cz-ti-fill', `${filled}%`);
    }

    const link = this.querySelector('[data-cta]');
    if (link instanceof HTMLAnchorElement && link.dataset.base) {
      const params = new URLSearchParams({
        brand: choice.brand,
        year: choice.year,
        cond: choice.condition,
        price: String(choice.price),
      });
      link.href = `${link.dataset.base}?${params}`;
    }
  };
}

const STORAGE_KEY = 'cz-trade-in-request';

class CzTradeInForm extends HTMLElement {
  connectedCallback() {
    this.config = readConfig();
    const form = this.querySelector('form');

    if (form) {
      this.prefill(form);
      form.addEventListener('input', this.update);
      form.addEventListener('change', this.update);
      form.addEventListener('submit', this.onSubmit);
      this.update();
    }

    this.showSuccess();
  }

  /** Fills the fields from the calculator's link. @param {HTMLFormElement} form */
  prefill(form) {
    const params = new URLSearchParams(window.location.search);
    /** @type {Record<string, string>} */
    const map = { brand: 'brand', year: 'year', cond: 'cond', price: 'price', model: 'model' };
    for (const [param, field] of Object.entries(map)) {
      const value = params.get(param);
      const input = form.querySelector(`[data-field="${field}"]`);
      if (!value || !(input instanceof HTMLInputElement || input instanceof HTMLSelectElement)) continue;
      if (input instanceof HTMLSelectElement && ![...input.options].some((option) => option.value === value)) continue;
      input.value = value;
    }
  }

  /** @param {string} field */
  field(field) {
    const input = this.querySelector(`[data-field="${field}"]`);
    return input instanceof HTMLInputElement || input instanceof HTMLSelectElement ? input.value.trim() : '';
  }

  update = () => {
    const config = this.config || readConfig();
    const brand = this.field('brand');
    const model = this.field('model');
    const year = this.field('year');
    const condition = this.field('cond');
    const price = Number(this.field('price').replace(/\D/g, '')) || 0;
    const result = estimate(config, { brand, year, condition, price });
    const kit = [...this.querySelectorAll('[data-kit]:checked')].map((input) =>
      input instanceof HTMLInputElement ? input.dataset.kit || '' : ''
    );
    const { individualText = 'індивідуально', noPriceText = 'Вкажіть ціну нової — покажемо суму', shareText = '≈ [pct] від [price] · точну суму назве експерт' } =
      this.dataset;

    setText(this, '[data-title]', [brand === 'Інший' ? '' : brand, model].filter(Boolean).join(' ') || '—');
    setText(
      this,
      '[data-summary]',
      [year, condition && `стан «${condition}»`, kit.join(', ').toLowerCase()].filter(Boolean).join(' · ')
    );

    let amount = individualText;
    let note = '';
    if (!result.individual && price > 0) {
      amount = `${formatMoney(result.low)} – ${formatMoney(result.high)}`;
      note = shareText.replace('[pct]', `${Math.round(result.share * 100)} %`).replace('[price]', formatMoney(price));
    } else if (!result.individual) {
      amount = `≈ ${Math.round(result.share * 100)} %`;
      note = noPriceText;
    }
    setText(this, '[data-estimate]', amount);
    setText(this, '[data-estimate-note]', note);

    const hidden = this.querySelector('[data-estimate-field]');
    if (hidden instanceof HTMLInputElement) hidden.value = note ? `${amount} (${note})` : amount;
  };

  onSubmit = () => {
    // A short number the buyer quotes in Telegram with the photos: TR-<day><month>-<3 digits>.
    const now = new Date();
    const number = `TR-${String(now.getDate()).padStart(2, '0')}${String(now.getMonth() + 1).padStart(2, '0')}-${Math.floor(100 + Math.random() * 900)}`;
    const hidden = this.querySelector('[data-number-field]');
    if (hidden instanceof HTMLInputElement) hidden.value = number;
    const title = [this.field('brand'), this.field('model')].filter(Boolean).join(' ');
    try {
      sessionStorage.setItem(STORAGE_KEY, JSON.stringify({ number, title }));
    } catch {
      // Private mode: the success screen just goes without the number.
    }
  };

  showSuccess() {
    const success = this.querySelector('[data-success]');
    if (!success) return;
    /** @type {{ number?: string, title?: string }} */
    let saved = {};
    try {
      saved = JSON.parse(sessionStorage.getItem(STORAGE_KEY) || '{}');
    } catch {
      saved = {};
    }
    if (saved.number) {
      setText(success, '[data-number]', saved.number);
      success.querySelectorAll('[data-number-wrap]').forEach((element) => element.removeAttribute('hidden'));
    }
    if (saved.title) setText(success, '[data-racket]', saved.title);

    // Telegram opens the chat with the request number already typed.
    const text = [saved.number && `Заявка ${saved.number}`, saved.title, 'Надсилаю фото ракетки']
      .filter(Boolean)
      .join(' · ');
    success.querySelectorAll('a[data-telegram]').forEach((link) => {
      if (!(link instanceof HTMLAnchorElement)) return;
      try {
        const url = new URL(link.href);
        if (url.hostname === 't.me' && url.pathname.length > 1) {
          url.searchParams.set('text', text);
          link.href = url.toString();
        }
      } catch {
        // Leave the link as it is.
      }
    });
  }
}

if (!customElements.get('cz-trade-in-calc')) customElements.define('cz-trade-in-calc', CzTradeInCalc);
if (!customElements.get('cz-trade-in-form')) customElements.define('cz-trade-in-form', CzTradeInForm);
